import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ArrowRight, CheckCircle, Star } from 'lucide-react';

const steps = [
  { step: 1, title: 'Submit Application', desc: 'Complete the online application form with organization and program details.' },
  { step: 2, title: 'Document Review', desc: 'IMCAC reviews submitted documentation for completeness and eligibility.' },
  { step: 3, title: 'Educational Assessment', desc: 'Educational content, design, and quality are evaluated against IMCAC standards.' },
  { step: 4, title: 'CPD Credit Allocation', desc: 'CPD credit values are determined based on verified learning hours and methodology.' },
  { step: 5, title: 'Recognition Decision', desc: 'The recognition level is determined and communicated to the applicant.' },
  { step: 6, title: 'Recognition Certificate Issued', desc: 'A formal CPD Recognition certificate is issued upon successful review.' },
];

const providerTiers = [
  {
    tier: 'Standard CPD Provider',
    price: 'USD $2,500',
    period: 'per year',
    desc: 'Suitable for hospitals, clinics, training institutes, professional societies, and educational organizations offering CPD activities primarily within one country or region.',
    badge: 'bg-blue-100 text-blue-800',
    benefits: [
      'Annual IMCAC CPD Provider Recognition',
      'Unlimited submission of eligible educational activities for CPD review during the recognition period',
      'Unlimited learner CPD certificates generated through the IMCAC platform',
      'Listing in the IMCAC Global CPD Provider Directory',
      'Permission to use the IMCAC CPD Recognized Provider logo in accordance with brand guidelines',
      'Access to the IMCAC provider dashboard',
      'Standard application review timelines',
      'Technical support',
    ],
  },
  {
    tier: 'International CPD Provider',
    price: 'USD $5,000',
    period: 'per year',
    desc: 'Designed for organizations delivering educational activities across multiple countries or to an international audience.',
    badge: 'bg-[#C5A572]/20 text-[#9A7D4E]',
    featured: true,
    benefits: [
      'All Standard Provider benefits',
      'Priority review of submitted educational activities',
      'Enhanced profile in the IMCAC Global CPD Provider Directory',
      'International recognition status',
      'Dedicated account support',
      'Recognition for multilingual and global educational activities (where applicable)',
    ],
  },
  {
    tier: 'Strategic CPD Partner',
    price: 'Starting from USD $10,000',
    period: 'per year',
    desc: 'Designed for universities, international healthcare organizations, professional associations, accreditation bodies, multinational education providers, and large healthcare networks seeking a long-term strategic collaboration with IMCAC.',
    badge: 'bg-[#1B2A4A] text-white',
    benefits: [
      'All International Provider benefits',
      'Dedicated relationship manager',
      'Priority processing for all submissions',
      'Customized recognition framework for large educational portfolios',
      'Joint professional development initiatives (where appropriate)',
      'Featured partner profile on the IMCAC website',
      'Opportunity to participate in IMCAC advisory and collaborative initiatives, subject to invitation',
      'Customized service agreement based on organizational needs',
    ],
  },
];

const conditions = [
  'Recognition is valid for 12 months from the date of approval.',
  'Recognition applies only to educational activities that successfully meet IMCAC\'s CPD Recognition Standards.',
  'IMCAC reserves the right to request revisions or additional documentation before granting recognition.',
  'IMCAC CPD Recognition does not constitute institutional accreditation, governmental approval, academic accreditation, professional licensure, or degree-awarding authority.',
  'Annual renewal is required to maintain provider recognition and continued use of the IMCAC CPD Recognized Provider designation.',
];

export default function CPDRecognitionFeesPage() {
  return (
    <div data-testid="cpd-recognition-fees-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">CPD Recognition</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">IMCAC CPD Recognition Provider Program</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            Annual recognition fees and application process for organizations seeking IMCAC CPD Provider status.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-5">
        <div className="imcac-container">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              IMCAC CPD Recognition evaluates educational activities for Continuing Professional Development purposes only. Recognition does not constitute governmental approval, university accreditation, specialist recognition, professional licensure, degree granting authority, or regulatory endorsement.
            </p>
          </div>
        </div>
      </section>

      {/* Annual Recognition Fees */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="text-center mb-10">
            <p className="imcac-overline mb-3">Provider Programs</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1B2A4A] font-heading">Annual Recognition Fees</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {providerTiers.map((tier, i) => (
              <Card key={i} className={`imcac-card border-t-4 ${tier.featured ? 'border-t-[#C5A572] ring-2 ring-[#C5A572]/20' : 'border-t-[#1B2A4A]'} relative`} data-testid={`fee-tier-${i}`}>
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#C5A572] text-white text-xs px-3"><Star className="h-3 w-3 mr-1" /> Popular</Badge>
                  </div>
                )}
                <CardContent className="p-6">
                  <Badge className={`${tier.badge} mb-3`}>{tier.tier}</Badge>
                  <div className="mb-4">
                    <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{tier.price}</p>
                    <p className="text-xs text-slate-500">{tier.period}</p>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{tier.desc}</p>
                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-semibold text-[#1B2A4A] uppercase tracking-wider mb-3">Benefits</p>
                    <div className="space-y-2.5">
                      {tier.benefits.map((benefit, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-slate-600 leading-relaxed">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Link to="/cpd-recognition/apply" className="block mt-6">
                    <Button className={`w-full rounded-sm ${tier.featured ? 'bg-[#C5A572] hover:bg-[#B09260] text-white' : 'bg-[#1B2A4A] hover:bg-[#152039] text-white'}`}>
                      Apply Now <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-8 text-center">Application Process</h2>
          <div className="max-w-3xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-4 mb-6" data-testid={`process-step-${step.step}`}>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-[#C5A572]">{step.step}</span>
                  </div>
                  {i < steps.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
                </div>
                <div className="pb-4">
                  <h3 className="text-base font-semibold text-[#1B2A4A] font-heading">{step.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General Conditions */}
      <section className="imcac-section bg-white">
        <div className="imcac-container max-w-4xl">
          <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-6">General Conditions</h2>
          <div className="space-y-3">
            {conditions.map((condition, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-sm">
                <span className="w-6 h-6 bg-[#1B2A4A] rounded-sm flex items-center justify-center text-xs font-bold text-[#C5A572] flex-shrink-0">{i + 1}</span>
                <p className="text-sm text-slate-700">{condition}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1B2A4A] py-12">
        <div className="imcac-container text-center">
          <h2 className="text-2xl font-bold text-white font-heading mb-4">Ready to Apply?</h2>
          <p className="text-sm text-slate-300 mb-6 max-w-xl mx-auto">
            Submit your application to become an IMCAC CPD Recognized Provider.
          </p>
          <Link to="/cpd-recognition/apply" data-testid="fees-apply-btn">
            <Button className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm px-8">
              Apply for CPD Recognition <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
